import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataContext } from '../contexts/DataContext';
import Pagination from '../utils/Pagination';
import logo from "../wisdom.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faSortUp, faSortDown, faAngleUp, faMoneyBillWave, faFileInvoiceDollar, faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons';
import MiniDashboardPaymentAdvances from './MinidashboardPaymentAdvances';
import Select from 'react-select';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import axios from 'axios';

const PaymentAdvanceGrid = () => {
    const { paymentAdvances, fetchAllPaymentAdvances } = useContext(DataContext);
    const [filteredAdvances, setFilteredAdvances] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100); // Adjust based on your preference
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isExpanded, setIsExpanded] = useState(false);
    const [customerNameOptions, setCustomerNameOptions] = useState([]);
    const [referenceOptions, setReferenceOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [showModal, setShowModal] = useState(false); // Add this line
    const [modalContent, setModalContent] = useState('');


    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);

    const [filters, setFilters] = useState({
        customerNames: [],
        references: [],
        createdDateRange: { start: null, end: null },
        statuses: [],
    });

    const navigate = useNavigate();

    const handleStatusUpdate = (id, newStatus) => {
        setFilteredAdvances(currentAdvances =>
            currentAdvances.map(advance =>
                advance._id === id ? { ...advance, status: newStatus } : advance
            )
        );
        setShowModal(true);
    };

    const toggleDateRangePicker = () => {
        setShowDateRangePicker(!showDateRangePicker);
    };

    useEffect(() => {
        fetchAllPaymentAdvances();
    }, []);

    useEffect(() => {
        setFilteredAdvances(paymentAdvances);
    }, [paymentAdvances]);

    useEffect(() => {
        if (paymentAdvances && paymentAdvances.length > 0) {
            const uniqueCustomerNames = [...new Set(paymentAdvances.map(advance => advance.customer_data?.customer_name).filter(name => name))];
            const uniqueReferences = [...new Set(paymentAdvances.map(advance => advance.traffic_reference))];
            const uniqueStatuses = [...new Set(paymentAdvances.map(advance => advance.status))];

            setCustomerNameOptions(uniqueCustomerNames.map(name => ({ value: name, label: name })));
            setReferenceOptions(uniqueReferences.map(ref => ({ value: ref, label: ref })));
            setStatusOptions(uniqueStatuses.map(status => ({ value: status, label: status })));
        }
    }, [paymentAdvances]);


    useEffect(() => {
        const filteredData = paymentAdvances.filter(advance => {
            const matchesCustomerName = !filters.customerNames.length || filters.customerNames.includes(advance.customer_data?.customer_name);
            const matchesReference = !filters.references.length || filters.references.includes(advance.traffic_reference);
            const matchesStatus = !filters.statuses.length || filters.statuses.includes(advance.status);
            const matchesDateRange = !filters.createdDateRange.start || (new Date(advance.created_date) >= filters.createdDateRange.start && new Date(advance.created_date) <= filters.createdDateRange.end);

            return matchesCustomerName && matchesReference && matchesStatus && matchesDateRange;
        });

        setFilteredAdvances(filteredData);
    }, [paymentAdvances, filters]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleDateRangeChange = (ranges) => {
        const { selection } = ranges;
        setDateRange([selection]);
        setFilters((prevFilters) => ({
            ...prevFilters,
            createdDateRange: {
                start: selection.startDate,
                end: selection.endDate,
            },
        }));
    };

    const handleEdit = (advance) => {
        if (advance && advance._id) {
            navigate('/paymentAdvance', { state: { rowData: advance, paymentAdvanceId: advance._id } });
        }
    };

    const getAgingClasses = (createdDate) => {
        const today = new Date();
        const created = new Date(createdDate);
        const differenceInTime = today.getTime() - created.getTime();
        const differenceInDays = differenceInTime / (1000 * 3600 * 24);

        if (differenceInDays <= 3) {
            return "bg-green-200 text-green-800"; // Light green background with darker green text for 1-3 days
        } else if (differenceInDays <= 5) {
            return "bg-yellow-200 text-yellow-800"; // Yellow background with darker yellow text for 3-5 days
        } else {
            return "bg-red-200 text-red-800"; // Red background with darker red text for more than 5 days
        }
    };

    // Calculate statistics
    const openAdvances = paymentAdvances.filter(advance => advance.status.toLowerCase() !== "closed");

    const totalOpen = paymentAdvances.filter(advance => advance.status !== "closed").length;
    const totalClosed = paymentAdvances.filter(advance => advance.status === "closed").length;
    const totalOpenedAmount = openAdvances.reduce((acc, curr) => acc + curr.total_requested_payment_advance, 0);
    const formattedTotalOpenedAmount = `$${totalOpenedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const averageAging = openAdvances.reduce((acc, curr) => acc + ((new Date() - new Date(curr.created_date)) / (1000 * 60 * 60 * 24)), 0) / openAdvances.length;

    const sortAdvances = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        } else {
            direction = 'ascending';
        }

        let sorted;
        if (key !== "aging") {
            sorted = [...filteredAdvances].sort((a, b) => {
                const aVal = key.includes(".") ? key.split(".").reduce((acc, part) => acc?.[part], a) : a[key];
                const bVal = key.includes(".") ? key.split(".").reduce((acc, part) => acc?.[part], b) : b[key];
                const valueA = aVal ?? '';
                const valueB = bVal ?? '';
                return (valueA < valueB) ? (direction === 'ascending' ? -1 : 1) :
                    (valueA > valueB) ? (direction === 'ascending' ? 1 : -1) : 0;
            });
        } else {
            // Special sorting logic for aging
            sorted = [...filteredAdvances].sort((a, b) => {
                const aDays = (new Date() - new Date(a.created_date)) / (1000 * 3600 * 24);
                const bDays = (new Date() - new Date(b.created_date)) / (1000 * 3600 * 24);
                return (direction === 'ascending' ? aDays - bDays : bDays - aDays);
            });
        }

        setFilteredAdvances(sorted);
        setSortConfig({ key, direction });
    };

    // Function to render the header with sorting capabilities
    const renderHeader = () => {
        const headers = [
            { label: "Reference", key: "traffic_reference" },
            { label: "Customer Name", key: "customer_data.customer_name" },
            { label: "Created", key: "created_date" },
            { label: "Aging", key: "aging" },
            { label: "Total Requested", key: "total_requested_payment_advance" },
            { label: "Actual Payment", key: "actual_payment_advance" },
            { label: "Status", key: "status" },
            { label: "Actions", key: "actions" },
        ];

        return (
            <div className="grid grid-cols-1 md:grid-cols-8 text-center font-semibold bg-gray-200 p-2 rounded-t-lg">
                {headers.map((header) => (
                    <div key={header.key} onClick={() => sortAdvances(header.key)} className="flex justify-center items-center cursor-pointer space-x-1">
                        <span>{header.label}</span>
                        {sortConfig.key === header.key && (
                            <FontAwesomeIcon icon={sortConfig.direction === 'ascending' ? faSortUp : faSortDown} />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const PaymentAdvanceCard = ({ advance, onStatusUpdate }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const toggleDetails = () => setIsExpanded(!isExpanded);
        const [isStatusToggled, setIsStatusToggled] = useState(advance.status === "review");

        // Initialize slider states based on the advance's current status
        const [documentsClosed, setDocumentsClosed] = useState(advance.status === "documents_closed");
        const [advanceClosed, setAdvanceClosed] = useState(advance.status === "closed");


        const handleToggleDocumentsClosed = async () => {
            // Determine the new status based on the current state
            let newStatus = documentsClosed ? "open" : "documents_closed";

            try {
                // Make the API call to update the payment advance status
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/update_payment_document_status`, {
                    documentId: advance._id,
                    newStatus, // "documents_closed" or revert to "open"
                    documentType: 'PaymentAdvance',
                });

                // If the API call is successful, update the component's state
                setDocumentsClosed(!documentsClosed);
                // Optionally, you can update the payment advance list in the parent component
                onStatusUpdate(advance._id, newStatus);
                // Provide feedback to the user
                setShowModal(true);
                setModalContent(`Status updated to "${newStatus}".`);
            } catch (error) {
                // Handle any errors that occur during the API call
                setShowModal(true);
                setModalContent(`Error updating status: ${error.response?.data?.message || error.message}`);
            }
        };

        const handleToggleAdvanceClosed = async () => {
            // Determine the new status based on the current state
            let newStatus = advanceClosed ? "open" : "closed";

            try {
                // Make the API call to update the payment advance status
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/update_payment_document_status`, {
                    documentId: advance._id,
                    newStatus, // "closed" or revert to "open"
                    documentType: 'PaymentAdvance',
                });

                // If the API call is successful, update the component's state
                setAdvanceClosed(!advanceClosed);
                // Ensure that if we are setting the status to "closed", the documentsClosed flag is also set
                if (newStatus === "closed") {
                    setDocumentsClosed(true);
                }
                // Optionally, you can update the payment advance list in the parent component
                onStatusUpdate(advance._id, newStatus);
                // Provide feedback to the user
                setShowModal(true);
                setModalContent(`Status updated to "${newStatus}".`);
            } catch (error) {
                // Handle any errors that occur during the API call
                setShowModal(true);
                setModalContent(`Error updating status: ${error.response?.data?.message || error.message}`);
            }
        };


        // Check if customer_data and customer_name exist
        const customerName = advance.customer_data && advance.customer_data.customer_name
            ? advance.customer_data.customer_name
            : 'N/A'; // Default value if not present

        // Helper function to get the appropriate styling classes for the status
        const getStatusClasses = (status) => {
            const baseClasses = "inline-flex items-center justify-center rounded px-2 py-1 capitalize font-semibold";
            if (!status) return `${baseClasses}`; // Add a default styling if status is not provided

            const statusClasses = {
                'open': "bg-yellow-200 text-yellow-800",
                'review': "bg-orange-200 text-orange-800",
                'advance_received': "bg-purple-200 text-purple-800",
                'documents_closed': "bg-blue-200 text-blue-800",
                'closed': "bg-green-200 text-green-800",
            };

            // Default to an empty string if the status is not one of the expected values
            return `${baseClasses} ${statusClasses[status.toLowerCase()] || ''}`;
        };


        // Function to format the date
        const formatDate = (dateString) => {
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(dateString).toLocaleDateString('en-EN', options);
        };

        const renderDetailsSection = (details, title, icon) => {
            const total = details.reduce((sum, detail) => sum + detail.amount, 0);

            return (
                <div className="flex-1 bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={icon} className="text-blue-500 mr-2" />
                        <span className="font-bold text-blue-700">{title}</span>
                    </div>
                    {details.map((detail, index) => (
                        <div key={index} className="flex justify-between text-gray-700 text-sm py-1">
                            <span className="flex items-center">
                                <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-blue-500" />
                                {detail.concept}
                            </span>
                            <span className="font-semibold">${detail.amount.toLocaleString()}</span>
                        </div>
                    ))}
                    <div className="flex justify-between border-t border-gray-200 pt-2 font-bold text-gray-700">
                        <span>Total</span>
                        <span>${total.toLocaleString()}</span>
                    </div>
                </div>
            );
        };


        return (
            <div className="bg-white text-center p-4 m-2 rounded-lg shadow">
                <div className="grid grid-cols-1 md:grid-cols-8 gap-4 items-center text-xs leading-5 font-semibold">
                    <div className="font-semibold">{advance.traffic_reference}</div>
                    <div className="bg-white text-black px-2 py-1">{customerName}</div> {/* Display customer name with a check */}
                    <div className="inline-flex items-center justify-center bg-purple-200 text-purple-800 rounded px-2 py-1">
                        {formatDate(advance.created_date)}
                    </div>
                    <div className="font-semibold">
                        {advance.status.toLowerCase() !== "closed" && (
                            <span className={`rounded-full px-2 py-1 ${getAgingClasses(advance.created_date)}`}>
                                {Math.round((new Date() - new Date(advance.created_date)) / (1000 * 60 * 60 * 24))} days
                            </span>
                        )}
                    </div>
                    <div className="inline-flex items-center justify-center">${advance.total_requested_payment_advance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className="inline-flex items-center justify-center">${advance.actual_payment_advance.toLocaleString()}</div>
                    <div className={getStatusClasses(advance.status)}>
                        {advance.status ? advance.status.charAt(0).toUpperCase() + advance.status.slice(1) : 'N/A'}
                    </div>
                    <div className="flex gap-x-2 justify-center">
                        <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded" onClick={() => toggleDetails(advance._id)}>
                            More Details
                        </button>
                        {advance.status === "closed" && (
                            <button
                                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-2 rounded"
                                onClick={() => navigate('/paymentAdvance', {
                                    state: {
                                        rowData: advance,
                                        paymentAdvanceId: advance._id,
                                        isViewOnly: true // Indicating that the PaymentAdvance component should be in view-only mode
                                    }
                                })}
                            >
                                View
                            </button>
                        )}
                        {advance.status !== "closed" && ( // Conditionally render the Edit button
                            <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded" onClick={() => handleEdit(advance)}>
                                Edit
                            </button>
                        )}

                        {/* Slider for 'Documents Closed' */}
                        <label className="flex items-center cursor-pointer">
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={documentsClosed} onChange={handleToggleDocumentsClosed} />
                                <div className={`w-10 h-4 ${documentsClosed || advanceClosed ? 'bg-green-400' : 'bg-gray-400'} rounded-full shadow-inner`}></div>
                                <div className={`dot absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-1 transition ${documentsClosed || advanceClosed ? 'translate-x-full bg-green-500' : 'bg-gray-400'}`}></div>
                            </div>
                        </label>
                        <label className="flex items-center cursor-pointer">
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={advanceClosed} onChange={handleToggleAdvanceClosed} />
                                <div className={`w-10 h-4 ${advanceClosed ? 'bg-green-400' : 'bg-gray-400'} rounded-full shadow-inner`}></div>
                                <div className={`dot absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-1 transition ${advanceClosed ? 'translate-x-full bg-green-500' : 'bg-gray-400'}`}></div>
                            </div>
                        </label>

                    </div>
                </div>
                {isExpanded && (
                    <div className="flex justify-around mt-4 space-x-4">
                        {advance.tax_expenses && advance.tax_expenses.length > 0 &&
                            renderDetailsSection(advance.tax_expenses, "Tax Expenses", faMoneyBillWave)}
                        {advance.actual_expenses && advance.actual_expenses.length > 0 &&
                            renderDetailsSection(advance.actual_expenses, "Actual Expenses", faHandHoldingUsd)}
                        {advance.custom_brokerage_expenses && advance.custom_brokerage_expenses.length > 0 &&
                            renderDetailsSection(advance.custom_brokerage_expenses, "Custom Brokerage Expenses", faHandHoldingUsd)}
                    </div>
                )}
                {showModal && (
                    <div className="fixed z-50 inset-0 bg-opacity-30 overflow-y-auto h-full w-full" id="my-modal">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3 text-center">
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                    {/* Icon or image */}
                                </div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900">{modalContent}</h3>
                                <div className="mt-2 px-7 py-3">
                                    <p className="text-sm text-gray-500">
                                        {/* Additional details if needed */}
                                    </p>
                                </div>
                                <div className="items-center px-4 py-3">
                                    <button id="ok-btn" className="px-4 py-2 bg-green-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-300" onClick={() => setShowModal(false)}>
                                        OK
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };


    return (
        <>
            <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
                <div className="flex items-center">
                    <img src={logo} alt="Wisdom logo" className="h-12 mr-2" />
                </div>
                <div className="text-xl font-semibold text-gray-700">
                    Payment Advance List
                </div>
            </nav>
            <div className="container mx-auto p-6 bg-gray-100">

                <div className="flex gap-4 p-1 relative">
                    <Select
                        options={customerNameOptions}
                        isMulti
                        onChange={selectedOptions => setFilters(prev => ({ ...prev, customerNames: selectedOptions.map(option => option.value) }))}
                        placeholder="Filter by Customer Name"
                    />
                    <Select
                        options={referenceOptions}
                        isMulti
                        onChange={selectedOptions => setFilters(prev => ({ ...prev, references: selectedOptions.map(option => option.value) }))}
                        placeholder="Filter by Reference"
                    />
                    {/* Implement Date Range Picker */}
                    <Select
                        options={statusOptions}
                        isMulti
                        onChange={selectedOptions => setFilters(prev => ({ ...prev, statuses: selectedOptions.map(option => option.value) }))}
                        placeholder="Filter by Status"
                    />
                    <button
                        onClick={toggleDateRangePicker}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded"
                    >
                        Select Date Range {/* This button toggles the date picker */}
                    </button>

                    {showDateRangePicker && (
                        <div className="absolute top-full left-0 z-50 bg-white mt-1 rounded-md shadow-lg"> {/* Absolutely positioned date picker */}
                            <DateRangePicker
                                ranges={[dateRange[0]]}
                                onChange={handleDateRangeChange}
                                className="date-range"
                            />
                        </div>
                    )}
                </div>
                <div className="p-1"> {/* Add padding to the container */}
                    <MiniDashboardPaymentAdvances
                        totalOpen={totalOpen}
                        totalClosed={totalClosed}
                        totalOpenedAmount={formattedTotalOpenedAmount}
                        averageAging={averageAging}
                    />
                </div>

                <div className="mb-4">
                    {/* Implement search and filter UI here if needed */}
                </div>
                <Pagination
                    total={filteredAdvances.length}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
                <div className="overflow-x-auto">
                    {renderHeader()}
                    {filteredAdvances.length > 0 ? (
                        filteredAdvances.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((advance, index) => (
                            <PaymentAdvanceCard key={index} advance={advance} onStatusUpdate={handleStatusUpdate} />
                        ))
                    ) : (
                        <div className="text-center p-4">No payment advances found</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default PaymentAdvanceGrid;
